import moment from 'moment'
import { MarkerItem, TripItem as FullTripItem, RouteItem } from 'tripPlanner/types/tripItem'
import { PIN_COLORS, SELECTED_PIN_COLORS } from '../TripItemMapMarker'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'

type Trip = {
  items: Array<FullTripItem>
}

export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY'

export function getDateIndex(item: MarkerItem | RouteItem, trip?: Trip): number {
  if (!item.startDate || !trip?.items?.length) {
    return 0
  }

  const itemDateStr = moment(item.startDate).format(ISO_DATE_FORMAT)

  const sortedUniqueDates = [...new Set(
    trip.items
      .filter((ti: FullTripItem) => ti.startDate)
      .map((ti: FullTripItem) => moment(ti.startDate).format(ISO_DATE_FORMAT)),
  )].sort()

  return sortedUniqueDates.indexOf(itemDateStr)
}

export const savedActivePinColor = '#FFFFFF'
const savedPinColor = '#EF505F'

export function getPinColor(
  item: FullTripItem,
  trip?: Trip,
  active?: boolean,
): string {
  if (!item.startDate) {
    return active ? savedPinColor : savedActivePinColor
  }

  const dayOfWeek = moment(item.startDate).format('dddd').toUpperCase() as DayOfWeek
  const colourSet = active ? SELECTED_PIN_COLORS : PIN_COLORS
  return colourSet[dayOfWeek] || colourSet.SUNDAY
}

export function getItemNumber(item: FullTripItem, trip?: Trip): number {
  if (!trip?.items) return 1

  const { items } = trip
  function sortByItineraryRank(a: FullTripItem, b: FullTripItem): number {
    return a.itineraryRankString.localeCompare(b.itineraryRankString)
  }

  function sortBySummaryRank(a: FullTripItem, b: FullTripItem): number {
    return a.summaryRankString.localeCompare(b.summaryRankString)
  }

  function getIndex(list: Array<FullTripItem>): number {
    return list.findIndex((tripItem) => tripItem.id === item.id) + 1
  }

  if (!item.startDate) {
    const undatedItems = items
      .filter(
        (tripItem) =>
          !tripItem.startDate &&
          !('alternativeGroup' in tripItem && tripItem.alternativeGroup),
      )
      .sort(sortBySummaryRank)

    return getIndex(undatedItems)
  }

  const itemDate = moment(item.startDate).format(ISO_DATE_FORMAT)

  const sameDayItems = items
    .filter(
      (tripItem) =>
        tripItem.startDate &&
        moment(tripItem.startDate).format(ISO_DATE_FORMAT) === itemDate,
    )
    .sort(sortByItineraryRank)

  return getIndex(sameDayItems)
}

export function scrollToItem(
  item: MarkerItem,
): void {
  if (!item.startDate) {
    window.scrollTo({ top: 250, behavior: 'smooth' })
    return
  }
  scrollToNewItemWithRetry(item.id)
}
